import { KeyValuePair } from "client/Api/common";

// Provides a way to Map keys to values for which the key would be reference equal
// compared in the vanilla Map implementation (e.g. object). To achieve this the creator
// need to supplie a "keyNormalizer" function which converts the key to a string (which are value
// equal compared). NOTE: the "keyNormalizer" determines what keys are unique are which not. E.g.
// if you where to have the key 1 and "1" and the "keyNormalizer" would yield the same value
// (e.g. "1"), the key would be considered equal
export class NormalizedMap<TKey, TValue> implements Map<TKey, TValue> {

	private readonly _map: Map<string, KeyValuePair<TKey, TValue>>;
	private readonly _keyNormalizer: (key: TKey) => string;

	public get size(): number {
		return this._map.size;
	}

	public get [Symbol.toStringTag](): string {
		return "NormalizedMap";
	}

	public constructor(
		keyNormalizer: (key: TKey) => string,
		iterable?: Iterable<readonly [TKey, TValue]>
	) {
		this._keyNormalizer = keyNormalizer;
		this._map = new Map<string, KeyValuePair<TKey, TValue>>();

		if (iterable) {
			for (const kvp of iterable) {
				const [key, value] = kvp;
				const normalizedKey: string = this.normalizeKey(key);

				this._map.set(
					normalizedKey,
					{ key, value }
				);
			}
		}
	}

	public clear(): void {
		this._map.clear();
	}

	public delete(key: TKey): boolean {
		const normalizedKey: string = this.normalizeKey(key);
		return this._map.delete(normalizedKey);
	}

	public forEach(callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void, thisArg?: any): void {
		this._map.forEach(
			(value): void => callbackfn(
				value.value,
				value.key,
				this
			),
			thisArg
		);
	}

	public get(key: TKey): TValue | undefined {
		const normalizedKey: string = this.normalizeKey(key);
		return this._map.get(normalizedKey)
			?.value;
	}

	public has(key: TKey): boolean {
		const normalizedKey: string = this.normalizeKey(key);
		return this._map.has(normalizedKey);
	}

	public set(key: TKey, value: TValue): this {
		const normalizedKey: string = this.normalizeKey(key);

		this._map.set(
			normalizedKey,
			{ key, value }
		);

		return this;
	}

	public entries(): IterableIterator<[TKey, TValue]> {
		return createIterator(
			this._map,
			(key, value) => [key, value]
		);
	}

	public keys(): IterableIterator<TKey> {
		return createIterator(
			this._map,
			(key) => key
		);
	}

	public values(): IterableIterator<TValue> {
		return createIterator(
			this._map,
			(_, value) => value
		);
	}

	public [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
		return this.entries();
	}

	private normalizeKey(key: TKey): string {
		return this._keyNormalizer(key);
	}
}

function createIterator<TKey, TValue, TReturn>(
	map: Map<string, KeyValuePair<TKey, TValue>>,
	selector: (key: TKey, value: TValue) => TReturn
): IterableIterator<TReturn> {
	const kvpIterator: IterableIterator<KeyValuePair<TKey, TValue>> = map.values();

	const result: IterableIterator<TReturn> = {
		[Symbol.iterator]() {
			return result;
		},
		next: (): IteratorResult<TReturn> => {
			const kvpResult: IteratorResult<KeyValuePair<TKey, TValue>, any> = kvpIterator.next();
			if (kvpResult.done) {
				return {
					done: true,
					value: undefined
				};
			}

			const kvp: KeyValuePair<TKey, TValue> = kvpResult.value;

			return {
				done: false,
				value: selector(kvp.key, kvp.value)
			};
		}
	};

	return result;
}
